import { Component, effect, inject, signal } from '@angular/core';
import { ApiService } from '../../api.service';
import { AuthGuardService } from '../../auth-guard.service';
import { FluidModule } from 'primeng/fluid';
import { PasswordModule } from 'primeng/password';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FloatLabelModule } from 'primeng/floatlabel';
import { MessageModule } from 'primeng/message';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TranslatePipe } from '@ngx-translate/core';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { APIError } from '../../../models/apierror';
import { TAGUser } from '../../../models/taguser';
import { RouterOutlet, RouterLink, RouterLinkActive } from '@angular/router';
import { InternalMenuService } from '../../internal-menu-service';
import { KeyValuePipe } from '@angular/common';
import { MatIcon } from '../../components/mat-icon/mat-icon';

type MenuLink = {
  route: string;
  label: string;
  icon: string;
  permission: string;
  disabled?: boolean;
};

@Component({
  selector: 'app-internal-base',
  imports: [
    ReactiveFormsModule,
    FluidModule,
    PasswordModule,
    FloatLabelModule,
    InputTextModule,
    MessageModule,
    ButtonModule,
    TranslatePipe,
    ProgressSpinnerModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    KeyValuePipe,
    MatIcon,
  ],
  templateUrl: './internal-base.html',
  styleUrl: './internal-base.scss',
})
export class InternalBase {
  private api = inject(ApiService);
  private authGuard = inject(AuthGuardService);
  private menu = inject(InternalMenuService);

  apiReady = signal(false);
  loggingIn = signal(false);
  loginError = signal('');

  menuOpen = signal(false);
  shadowVisible = signal(false);

  loginForm = new FormGroup({
    username: new FormControl('', [Validators.required]),
    password: new FormControl('', [Validators.required]),
  });

  internalMenuEntries: { [key: string]: MenuLink[] } = {
    '': [
      {
        route: '/internal/dashboard',
        label: 'internal.menu.links.dashboard',
        icon: 'home',
        permission: 'dashboard',
      },
    ],
    daily: [
      {
        route: '/internal/todos',
        label: 'internal.menu.links.todos',
        icon: 'checklist',
        permission: 'todos',
      },
      {
        route: '/internal/presence',
        label: 'internal.menu.links.presence',
        icon: 'sensor_occupied',
        permission: 'presence',
      },
      {
        route: '/internal/requests',
        label: 'internal.menu.links.requests',
        icon: 'calendar_clock',
        permission: 'requests',
      },
      {
        route: '/internal/events',
        label: 'internal.menu.links.events',
        icon: 'event',
        permission: 'events',
      },
      {
        route: '/internal/songrequests',
        label: 'internal.menu.links.songrequests',
        icon: 'music_note_add',
        permission: 'songrequests',
      },
    ],
    management: [
      {
        route: '/internal/gallery',
        label: 'internal.menu.links.gallery',
        icon: 'photo_prints',
        permission: 'gallery',
      },
      {
        route: '/internal/inventory',
        label: 'internal.menu.links.inventory',
        icon: 'shelves',
        permission: 'inventory',
      },
      {
        route: '/internal/finances',
        label: 'internal.menu.links.finances',
        icon: 'account_balance',
        permission: 'finances',
      },
      {
        route: '/internal/radio',
        label: 'internal.menu.links.radio',
        icon: 'podcasts',
        permission: 'schulradio',
        disabled: true,
      },
    ],
    settings: [
      {
        route: '/internal/users',
        label: 'internal.menu.links.users',
        icon: 'manage_accounts',
        permission: 'users',
      },
      {
        route: '/internal/settings',
        label: 'internal.menu.links.settings',
        icon: 'settings',
        permission: 'settings',
      },
    ],
  };

  get filteredMenuEntries() {
    let filtered: { [key: string]: MenuLink[] } = {};
    for (let group in this.internalMenuEntries) {
      let filteredItems = this.internalMenuEntries[group].filter((entry) => {
        return this.hasPermission(entry.permission);
      });

      if (filteredItems.length > 0) {
        filtered[group] = filteredItems;
      }
    }
    return filtered;
  }

  constructor() {
    this.api.waitForAPI().then(() => {
      this.apiReady.set(true);
    });

    effect(() => {
      if (this.menu.isOpen()) {
        this.shadowVisible.set(true);
        setTimeout(() => {
          this.menuOpen.set(true);
        }, 100);
      } else {
        this.menuOpen.set(false);
        setTimeout(() => {
          this.shadowVisible.set(false);
        }, 300);
      }
    });
  }

  ngOnInit() {}

  hasPermission(group: string): boolean | Promise<boolean> {
    return this.authGuard.canActivateUrl(group);
  }

  hasEmail(): boolean {
    return this.api.user().technikEmail != null && this.api.user().technikEmail != '';
  }

  isLoggedIn() {
    return this.api.isLoggedIn();
  }

  login() {
    if (!this.loginForm.valid) return;

    this.loggingIn.set(true);
    this.loginError.set('');

    console.log(this.loginForm.value);

    this.api
      .login(this.loginForm.value.username, this.loginForm.value.password)
      .then((result: TAGUser) => {
        this.loggingIn.set(false);
        this.loginError.set('');
      })
      .catch((reason: APIError) => {
        console.log(reason);
        this.loggingIn.set(false);
        if (reason.error == 2) {
          this.loginError.set('general.login.error.invalid');
        } else if (reason.error == 3 || reason.error == 7) {
          this.loginError.set('general.login.error.wronguser');
        } else {
          if (reason.status == 400 || reason.status == 403) this.loginError.set(reason.message);
          else this.loginError.set('Error ' + reason.error + ': ' + reason.message);
        }
      });
  }

  logout() {
    this.api.logout();
  }

  closeMenu() {
    this.menu.toggleMenu(false);
  }
}
